import { useQuery } from 'react-query';
import { apiClient, endpoints } from '../api/client';

export interface FieldCoverage {
  present: string[];
  empty: string[];
  not_configured: string[];
  present_count: number;
  expected_count: number;
  sampled: boolean;
}

export type SuiteOutcome = 'passed' | 'partial' | 'failed' | 'skipped' | 'pending';

export interface DefinitionTestSuiteSummary {
  id: string;
  brand_name: string;
  keyword: string;
  status: string;
  task_group_id?: string | null;
  error_message?: string | null;
  created_by?: string | null;
  created_at?: string | null;
  completed_at?: string | null;
  item_count: number;
  finished_count: number;
}

export type AccessClass =
  | 'stable'
  | 'login_once'
  | 'captcha_every_time'
  | 'needs_definition'
  | 'unclassified';

export interface DefinitionTestSuiteItem {
  id: string;
  marketplace_definition_id: string;
  marketplace_name: string;
  domain: string;
  task_id?: string | null;
  search_url?: string;
  task_status?: string | null;
  outcome: SuiteOutcome;
  reason: string;
  search_listing_count: number;
  product_count: number;
  search_coverage: FieldCoverage;
  product_coverage: FieldCoverage;
  needs_work: boolean;
  access_class?: AccessClass;
}

export interface DefinitionTestSuiteReport {
  id: string;
  brand_name: string;
  keyword: string;
  status: string;
  task_group_id?: string | null;
  error_message?: string | null;
  created_by?: string | null;
  created_at?: string | null;
  completed_at?: string | null;
  summary: {
    total: number;
    passed: number;
    partial: number;
    failed: number;
    skipped: number;
    pending: number;
    needs_work: number;
    stable?: number;
    login_once?: number;
    captcha_every_time?: number;
    needs_definition?: number;
  };
  items: DefinitionTestSuiteItem[];
}

export interface DefinitionTestSuiteCreate {
  brand_name: string;
  keyword: string;
  created_by?: string;
}

export async function createDefinitionTestSuite(
  body: DefinitionTestSuiteCreate
): Promise<{ id: string; status: string }> {
  const response = await apiClient.post(endpoints.definitionTestSuites, body);
  return response.data;
}

export const useDefinitionTestSuiteList = () => {
  return useQuery<{ items: DefinitionTestSuiteSummary[] }>(
    'definitionTestSuites',
    async () => {
      const response = await apiClient.get(endpoints.definitionTestSuites);
      return response.data;
    },
    { refetchInterval: 10000 }
  );
};

export interface DefinitionTestSuiteTrendPoint {
  id: string;
  brand_name: string;
  keyword: string;
  status: string;
  created_at?: string | null;
  label: string;
  attempted: number;
  skipped: number;
  passed: number;
  pass_rate: number;
}

export async function deleteDefinitionTestSuite(id: string): Promise<void> {
  await apiClient.delete(endpoints.definitionTestSuite(id));
}

export const useDefinitionTestSuiteTrend = () => {
  return useQuery<{ items: DefinitionTestSuiteTrendPoint[] }>(
    'definitionTestSuiteTrend',
    async () => {
      const response = await apiClient.get(endpoints.definitionTestSuiteTrend);
      return response.data;
    },
    { refetchInterval: 60000, staleTime: 30000 }
  );
};

export const useDefinitionTestSuite = (suiteId: string, live: boolean) => {
  return useQuery<DefinitionTestSuiteReport>(
    ['definitionTestSuite', suiteId],
    async () => {
      const response = await apiClient.get(endpoints.definitionTestSuite(suiteId));
      return response.data;
    },
    {
      enabled: Boolean(suiteId),
      refetchInterval: live ? 5000 : false,
    }
  );
};
